// cva(): Varianten-Klassen nach dem Vorbild von class-variance-authority (gleiche API für
// `variants`, `defaultVariants`, `compoundVariants`, `VariantProps`). Behelf, bis das Paket
// installiert ist (UI-Wunsch ui-basis); danach reicht ein Import-Tausch.
import { type ClassValue, cn } from './cn';

type VariantMap = Record<string, Record<string, ClassValue>>;

type BoolKey<K> = K extends 'true' | 'false'
  ? boolean
  : K extends `${infer N extends number}`
    ? N | K
    : K;

export type VariantSelection<V extends VariantMap> = {
  [K in keyof V]?: BoolKey<`${keyof V[K] & (string | number)}`> | null;
};

export interface CvaConfig<V extends VariantMap> {
  variants?: V;
  defaultVariants?: VariantSelection<V>;
  compoundVariants?: readonly (VariantSelection<V> & {
    class?: ClassValue;
    className?: ClassValue;
  })[];
}

export type CvaFn<V extends VariantMap> = (
  props?: VariantSelection<V> & { class?: ClassValue; className?: ClassValue },
) => string;

/** Props eines cva-Rezepts ohne class/className (wie `VariantProps` aus class-variance-authority). */
export type VariantProps<F> = F extends CvaFn<infer V> ? VariantSelection<V> : never;

const keyOf = (value: unknown): string | undefined =>
  value === undefined || value === null ? undefined : String(value);

export function cva<V extends VariantMap = Record<never, never>>(
  base: ClassValue,
  config: CvaConfig<V> = {},
): CvaFn<V> {
  const { variants = {} as V, defaultVariants = {}, compoundVariants = [] } = config;
  return (props = {}) => {
    const selected: Record<string, string | undefined> = {};
    for (const name of Object.keys(variants)) {
      const value = keyOf((props as Record<string, unknown>)[name]);
      selected[name] = value ?? keyOf((defaultVariants as Record<string, unknown>)[name]);
    }
    const variantClasses = Object.entries(variants).map(([name, options]) => {
      const choice = selected[name];
      return choice === undefined ? undefined : options[choice];
    });
    const compound = compoundVariants
      .filter(({ class: _c, className: _cn, ...conditions }) =>
        Object.entries(conditions).every(([name, value]) => selected[name] === keyOf(value)),
      )
      .map((entry) => [entry.class, entry.className]);
    return cn(base, variantClasses, compound, props.class, props.className);
  };
}
